
import React from 'react'
import { get, has, keyBy, map, union } from 'lodash'
import styled from 'styled-components'
import { productPickingType } from '../../../utils/misc'
import { generateColumns } from './AuditColumnsGenerator'
import { substituteColumns, excessColumns } from './AuditTableColumns'
import { GetUnitPrice, Number, Text, StatusTag, AppIcon, ConditionalRender, FlexContainer } from '../../common'

const _renderCell = (item, dataIndex) => (get(item, dataIndex, '-'))

const ClickableLink = styled.a`
    color: black;
`

export const auditHistoryColumns = [
  {
    title: 'Solicitado',
    dataIndex: 'quantity',
    key: 'quantity',
    columnStyle: { textAlign: 'left' },
    index: 1,
    render: (item, dataIndex) => {
      const quantity = get(item, dataIndex, '0')
      const unitMultiplier = get(item, 'unitMultiplier', '0')
      if (String(quantity) === '0') return _renderCell(null, null)
      const measureUnit = get(item, 'unit', 'un');
      const value = quantity * unitMultiplier;
      const valueToShow = measureUnit === 'kg' ? parseFloat(value).toFixed(3) : value;
      return `${(String(valueToShow)).replace('.', ',')} ${measureUnit}`;
    }
  },
  {
    title: 'Resultado picking',
    dataIndex: 'picking.pickingQuantity',
    width: 100,
    key: 'picked',
    index: 2,
    columnStyle: { textAlign: 'left' },
    render: (item, dataIndex) => {
      const status = get(item, 'picking.status')
      const shouldHide = ['FREE_PICKING_ACCEPTED', 'SUBSTITUTE_CONFIRMED']
      const quantity = get(item, dataIndex, '0')
      const measureUnit = get(item, 'unit', 'un')
      const value = get(item, dataIndex)
      const valueToShow = measureUnit === 'kg' ? parseFloat(value).toFixed(3) : value
      if (String(quantity) === '0' || shouldHide.includes(status)) return _renderCell(null, null)
      return `${(String(valueToShow).replace('.', ','))} ${measureUnit}`
    }
  },
  {
    title: 'Estado',
    dataIndex: 'status',
    key: 'status',
    index: 3,
    columnStyle: { textAlign: 'left' },
    render: (item, dataIndex) => {
      const status = productPickingType(get(item, 'picking.status', 'default'))
      return (
        <FlexContainer>
          <StatusTag mL={0} color='#1fa02e' key={dataIndex} status={`order_detail.item.picking.status.${status}`} tagType={status} />
        </FlexContainer>
      )
    }
  },
  {
    title: 'Regla',
    dataIndex: 'picking.productId.info',
    key: 'audit-rules',
    index: 4,
    width: 100,
    columnStyle: { textAlign: 'left' },
    render: (item, dataIndex, { auditInfo }) => {
      const filteredByDecision = get(item, 'auditItemResults.audits')
      return (
        <FlexContainer direction='column' width={100}>
          {map(filteredByDecision, ({ value, codeRule }) => (
            <StatusTag mL={0} key={dataIndex} status={`order_audit.status.${codeRule}`} tagType='error' />
          ))}
        </FlexContainer>
      )
    }
  },
  {
    title: 'Umbral',
    dataIndex: 'picking.productId.umbral',
    key: 'error-umbral',
    index: 5,
    columnStyle: { textAlign: 'left' },
    render: (item, dataIndex, { auditInfo }) => {
      const filteredByDecision = get(item, 'auditItemResults.audits')
      return (
        <FlexContainer direction='column'>
          {map(filteredByDecision, ({ value, codeRule }) => {
            if (codeRule !== '8') {
              return (
                <Text lineHeight={2.5} size={11} text={`${parseInt(value)} %`} />
              )
            }
            return <FlexContainer margin={19} />
          })}
        </FlexContainer>
      )
    }
  },
  {
    title: 'Auditoria',
    dataIndex: 'picking.productId.info',
    key: 'audit-rules',
    columnStyle: { textAlign: 'left' },
    index: 6,
    render: (item, dataIndex, { auditInfo }) => {
      return (
        <FlexContainer direction='column'>
          <Text text={get(item, `auditItemResults.failed`, true) ? 'Rechazado' : 'Aprobado'} />
        </FlexContainer>
      )
    }
  },
  {
    title: 'Producto',
    dataIndex: 'name',
    width: 150,
    key: 'product',
    columnStyle: { textAlign: 'left' },
    index: 7,
    render: (item, dataIndex, { onRowClick = () => (null) }) => (
      <div>
        <div>
          <p>{get(item, dataIndex, get(item, `picking.${dataIndex}`))}</p>
        </div>
        <ConditionalRender condition={has(item, 'priceTags.0.rateAndBenefit.name')}>
          <div style={{ display: 'flex' }}>
            <AppIcon name='ic-descuento' fill='#666666' style={{ marginRight: '3px', fontSize: '11px' }} />
            <Text text={get(item, 'priceTags.0.rateAndBenefit.name')} tiny color='#666666' />
          </div>
        </ConditionalRender>
      </div>
    )
  },
  {
    title: 'Precio unitario',
    dataIndex: 'basePrice',
    key: 'client',
    index: 8,
    render: (item, dataIndex) => (
      <GetUnitPrice item={item}>
        {({ priceTags, listPrice, price }) => (
          <>
            {
              priceTags && (
                <div>
                  <span>
                    <del style={{ color: '#666666' }}>
                      <Number value={listPrice} />
                    </del>
                  </span>
                </div>
              )
            }
            <div>
              <span>
                <Number value={price} />
              </span>
            </div>
          </>
        )}
      </GetUnitPrice>
    )
  },
  {
    title: 'Total solicitado',
    dataIndex: 'sellingPrice',
    key: 'sale_price',
    index: 9,
    render: (item, dataIndex) => {
      const { quantity = 0 } = item
      return (
        <div>
          <span>
            <Number value={get(item, dataIndex) * parseFloat(quantity)} />
          </span>
        </div>
      )
    }
  },
  {
    title: 'Total pickeado',
    dataIndex: 'picking',
    key: 'final_price',
    index: 10,
    render: (item, dataIndex) => {
      const data = get(item, dataIndex, {})
      const { sellingPrice, pickingQuantity, unitMultiplier } = data
      const value = parseInt(sellingPrice) * (parseFloat(pickingQuantity) / parseFloat(unitMultiplier))
      return (
        <span>
          { value > 0 ? <Number value={value} /> : '-' }
        </span>
      )
    }
  }
]

export const getColumns = () => {
  const columns = generateColumns([
    {
      key: 'parentColumns',
      scheme: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      columnSet: auditHistoryColumns
    },
    {
      key: 'excessColumns',
      scheme: [0, 0, 0, 0, 0, 0, 0, 0, 0, 1],
      columnSet: excessColumns
    },
    {
      key: 'substituteColumns',
      scheme: [4, 3, 0, 0, 0, 0, 1, 5, 6, 7],
      columnSet: substituteColumns
    }
  ])

  return columns
}
